import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import HourSelector from '../../../../../../../../components/common/HourSelector/HourSelector';
import './DeliveryApps.scss';

interface DeliveryAppsProps {
    setViewMode: any;
    setOrderContext: (orderContext: any) => void;
    handleClose: () => void;
    show: boolean;
}

export const DeliveryApps: React.FC<DeliveryAppsProps> = ({
    setViewMode,
    setOrderContext,
    handleClose,
    show
}) => {

    const [localClientName, setLocalClientName] = useState<string>('');
    const [localHoraEntrega, setLocalHoraEntrega] = useState<string>('');
    const [numeroOrden, setNumeroOrden] = useState<string>('');
    const [selectedQuickTime, setSelectedQuickTime] = useState<string>('');

    // Available providers: Uber Eats (1), Rappi (2), Pedidos Ya (3), JUSTO (4)
    const availableProviders = [
        { id: 1, name: 'Uber Eats', logo: 'uber-eats.png', className: 'uber-eats' },
        { id: 2, name: 'Rappi', logo: 'rappi.png', className: 'rappi' },
        { id: 3, name: 'Pedidos Ya', logo: 'pedidos-ya.png', className: 'pedidos-ya' },
        { id: 4, name: 'JUSTO', logo: 'JUSTO.png', className: 'justo' }
    ];

    // Estado para guardar el id del proveedor seleccionado
    const [selectedThirdPartyProviderId, setSelectedThirdPartyProviderId] = useState<number | ''>('');

    useEffect(() => {
        if (!show) {
            setViewMode('select');
            setLocalClientName('');
            setLocalHoraEntrega('');
            setNumeroOrden('');
            setSelectedQuickTime('');
            setSelectedThirdPartyProviderId('');
            setOrderContext({});
        }
    }, [show, setViewMode, setOrderContext]);

    const handleQuickTimeSelect = (minutes: number, label: string) => {
        const now = new Date();
        const totalMinutes = now.getHours() * 60 + now.getMinutes() + minutes;
        
        // Calculate new hours and minutes
        const newHours = Math.floor(totalMinutes / 60) % 24;
        const newMinutes = totalMinutes % 60;
        
        // Round minutes to nearest 5-minute interval
        const roundedMinutes = Math.round(newMinutes / 5) * 5;
        
        // Handle minute overflow (if rounding goes to 60)
        const finalHours = roundedMinutes >= 60 ? (newHours + 1) % 24 : newHours;
        const finalMinutes = roundedMinutes >= 60 ? 0 : roundedMinutes;
        
        const timeString = `${finalHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
        
        setLocalHoraEntrega(timeString);
        setSelectedQuickTime(label);
    };

    // Función para manejar el envío del formulario "App Externa"
    const handleAppSubmit = (e: React.FormEvent | React.MouseEvent) => {
        if ('preventDefault' in e) e.preventDefault();
        const today = new Date().toISOString().split('T')[0];
        const formattedHoraEntrega = `${today}T${localHoraEntrega}:00`;
        let orderContext = {
            client_id: null,
            client_name: localClientName,
            mesa_id: null,
            mesa_num: null,
            hora_entrega: formattedHoraEntrega,
            priority: null,
            third_party_provider_code: numeroOrden,
            third_party_provider_id: selectedThirdPartyProviderId,
        };
        setOrderContext(orderContext);
        setViewMode('order');
    };

    return (
        <>
            <Modal.Body className="form-section delivery-apps-section">
                <Form onSubmit={handleAppSubmit}>
                    <Form.Group controlId="clientName" className="mp-formgroup horizontal">
                        <Form.Label>Nombre del Cliente</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ingrese el nombre del cliente"
                            value={localClientName}
                            onChange={(e) => setLocalClientName(e.target.value)}
                            required
                        />
                    </Form.Group>
                    
                    <Form.Group controlId="horaEntrega" className="mp-formgroup horizontal">
                        <Form.Label>Hora de Entrega</Form.Label>
                        <HourSelector
                            id="horaEntrega"
                            value={localHoraEntrega}
                            onChange={(time) => {
                                setLocalHoraEntrega(time);
                                setSelectedQuickTime('');
                            }}
                            disabled={false}
                            allowPrev={false}
                        />
                    </Form.Group>

                    {!localHoraEntrega && (
                        <div className="quick-time-section">
                            <label className="quick-time-label">Selección Rápida</label>
                            <div className="quick-time-buttons">
                            <button
                                type="button"
                                className={`quick-time-btn ${selectedQuickTime === '15min' ? 'active' : ''}`}
                                onClick={() => handleQuickTimeSelect(15, '15min')}
                            >
                                15 min
                            </button>
                            <button
                                type="button"
                                className={`quick-time-btn ${selectedQuickTime === '30min' ? 'active' : ''}`}
                                onClick={() => handleQuickTimeSelect(30, '30min')}
                            >
                                30 min
                            </button>
                            <button
                                type="button"
                                className={`quick-time-btn ${selectedQuickTime === '45min' ? 'active' : ''}`}
                                onClick={() => handleQuickTimeSelect(45, '45min')}
                            >
                                45 min
                            </button>
                            <button
                                type="button"
                                className={`quick-time-btn ${selectedQuickTime === '1h' ? 'active' : ''}`}
                                onClick={() => handleQuickTimeSelect(60, '1h')}
                            >
                                1 hora
                            </button>
                        </div>
                    </div>
                    )}
                    
                    <Form.Group controlId="numeroOrden" className="mp-formgroup horizontal">
                        <Form.Label>Número de Orden</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ej: #12345"
                            value={numeroOrden}
                            onChange={(e) => setNumeroOrden(e.target.value)}
                            required
                        />
                    </Form.Group>
                    
                    <div className="provider-selection">
                        <Form.Label>Aplicación de Delivery</Form.Label>
                        <div className="provider-options">
                            {availableProviders.map((provider) => (
                                <div
                                    key={provider.id}
                                    className={`provider-option ${provider.className} ${
                                        selectedThirdPartyProviderId === provider.id ? 'active' : ''
                                    }`}
                                    onClick={() => setSelectedThirdPartyProviderId(provider.id)}
                                >
                                    <div className="provider-logo">
                                        <img 
                                            src={`${process.env.PUBLIC_URL}/icons/providers/${provider.logo}`}
                                            alt={provider.name}
                                        />
                                    </div>
                                    <span className="provider-name">{provider.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Form>
            </Modal.Body>
    
            <Modal.Footer>
                <div className="form-modal-footer-custom">
                    <Button variant="none" className="form-button-cancel" onClick={handleClose}>
                        Cancelar
                    </Button>
                    <Button 
                        className="form-button-create" 
                        variant="none" 
                        onClick={handleAppSubmit}
                        disabled={!localClientName || !localHoraEntrega || !numeroOrden || !selectedThirdPartyProviderId}
                    >
                        Tomar Orden
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
};

export default DeliveryApps;
